import { defineStore } from 'pinia';

import { IBingoState, type Card } from './bingo.state';

import allTask from './tasks.json';
import Tr from '../../i18n/translation';
import { useStateStore } from '../state.store.js';
import DataService from '../../services/DataService.js';
import { Logger } from '../../middlewares/sentry.js';
import BingoService from '@/services/BingoService.js';
import { setPlayerId } from './utils';
import { BingoDataClass } from 'interface/data/bingo/data';
import { BingoCommonClass } from 'interface/data/bingo/common';
import { BingoLangClass } from 'interface/data/bingo/lang';

const BINGO_SIZE = 3;

export const useBingoStore = defineStore('bingo', {
  state: (): IBingoState => ({
    clientData: new BingoDataClass(),
    bingoCards: [],
    bingoCount: 0,
    fullBingoCount: 0,
    playerName: localStorage.getItem('bingoPlayerName') || '',
    playerId: localStorage.getItem('bingoPlayerId') || setPlayerId(),
    isDataReceived: false,
    isLangDataReceived: false,
  }),
  actions: {
    async trySetupData() {
      await this._setBingoCommonData();
      const lang = Tr.currentLocale;
      this.tryGetLangDataWithImages(lang);
    },
    async _setBingoCommonData() {
      if (!this.isDataReceived) {
        await this._tryGetBingoCommonData();
        this.isDataReceived = true;
      } else {
        new Promise(async () => {
          this._tryGetBingoCommonData();
        });
      }

      const { setColors } = useStateStore();
      setColors(
        this.clientData.common.mainColor,
        this.clientData.common.mainColor,
        this.clientData.common.backgroundColor,
      );
    },
    async _tryGetBingoCommonData() {
      try {
        const data = await DataService.getBingoData();
        this.clientData.common = new BingoCommonClass(data);
      } catch (err) {
        Logger.error(err);
        if (!this.isDataReceived) {
          this.clientData.common = new BingoCommonClass();
        }
      }
    },
    async tryGetLangDataWithImages(lang: string) {
      await this._setGetLangData(lang);
    },
    async _setGetLangData(lang: string) {
      if (!this.isLangDataReceived) {
        await this.tryGetLangData(lang);
        this.isLangDataReceived = true;
      } else {
        new Promise(async () => {
          this.tryGetLangData(lang);
        });
      }
    },
    async tryGetLangData(lang: string) {
      try {
        const data = await DataService.getBingoData(lang);
        this.clientData.lang[lang] = new BingoLangClass(data);
      } catch (err) {
        Logger.error(err);
        if (!this.isLangDataReceived) {
          const { getDefaultLang } = useStateStore();

          if (lang !== getDefaultLang) {
            this.tryGetLangData(getDefaultLang);
            return;
          }

          this.clientData.lang[lang] = new BingoLangClass();
        }
      }
    },
    resetData() {
      this.clientData = new BingoDataClass();
      this.bingoCards = [];
      this.bingoCount = 0;
      this.fullBingoCount = 0;
      this.playerName = localStorage.getItem('bingoPlayerName') || '';
      this.playerId = localStorage.getItem('bingoPlayerId') || setPlayerId();
    },
    setPlayerName(playerName: string) {
      this.playerName = playerName;
      localStorage.setItem('bingoPlayerName', playerName);
    },
    __saveCardsStatus(bingoCards) {
      const storedCards = localStorage.getItem('bingoCards');
      let parsedCards: Record<string, Card[]> = {};
      if (storedCards) {
        parsedCards = JSON.parse(storedCards) as unknown as Record<
          string,
          Card[]
        >;
      }
      const { getClientId } = useStateStore();
      parsedCards[getClientId] = bingoCards;
      localStorage.setItem('bingoCards', JSON.stringify(parsedCards));
    },
    generateBingoCards() {
      const totalCards = BINGO_SIZE * BINGO_SIZE;
      const shuffledTasks = allTask.sort(() => Math.random() - 0.5);

      const cards: Card[] = [];
      for (let i = 0; i < totalCards; i++) {
        cards.push({
          id: i,
          value: shuffledTasks[i]?.task ?? '',
          isCompleted: false,
        });
      }

      this.bingoCards = cards;

      this.__saveCardsStatus(cards);
    },
    refreshBingoCards() {
      this.generateBingoCards();
    },
    loadBingoCards() {
      const storedCards = localStorage.getItem('bingoCards');
      if (!storedCards) {
        this.generateBingoCards();
        return;
      }

      const parsedCards = JSON.parse(storedCards) as unknown as Record<
        string,
        Card[]
      >;
      const { getClientId } = useStateStore();
      const playerCards = parsedCards[getClientId];
      if (!playerCards) {
        this.generateBingoCards();
        return;
      }

      this.bingoCards = playerCards;
    },
    toggleCardCompletion(cardId: number, isCompleted: boolean = true) {
      if (this.bingoCards[cardId] === undefined) {
        return;
      }

      this.bingoCards[cardId].isCompleted = isCompleted;

      this.__saveCardsStatus(this.bingoCards);

      if (this.bingoCards[cardId].isCompleted && this._checkIfBingo(cardId)) {
        this.bingoCount += 1;
      }

      const completedTasksNum = this.bingoCards.filter(
        (card) => card.isCompleted,
      ).length;

      if (completedTasksNum === 9) {
        this.fullBingoCount += 1;
      }

      BingoService.sendBingoInfo({
        playerId: this.playerId,
        playerName: this.playerName,
        bingoCount: this.bingoCount,
        fullBingoCount: this.fullBingoCount,
        completedTasksNum,
        updateTime: undefined,
      });
    },
    _checkIfBingo(cardIndex: number): boolean {
      if (cardIndex === -1 || !this.bingoCards[cardIndex]?.isCompleted) {
        return false;
      }

      const row = Math.floor(cardIndex / BINGO_SIZE);
      const col = cardIndex % BINGO_SIZE;

      if (
        this.bingoCards
          .slice(row * BINGO_SIZE, row * BINGO_SIZE + BINGO_SIZE)
          .every((card) => card.isCompleted)
      ) {
        return true;
      }

      let colCompleted = true;
      for (let r = 0; r < BINGO_SIZE; r++) {
        if (!this.bingoCards[r * BINGO_SIZE + col]?.isCompleted) {
          colCompleted = false;
          break;
        }
      }
      if (colCompleted) {
        return true;
      }

      if (row === col) {
        if (
          this.bingoCards.every(
            (_, index) =>
              index % (BINGO_SIZE + 1) === 0 &&
              this.bingoCards[index]?.isCompleted,
          )
        ) {
          return true;
        }
      }

      if (row + col === BINGO_SIZE - 1) {
        if (
          this.bingoCards.every(
            (_, index) =>
              index > 0 &&
              index % (BINGO_SIZE - 1) === 0 &&
              index < BINGO_SIZE * BINGO_SIZE - 1 &&
              this.bingoCards[index]?.isCompleted,
          )
        ) {
          return true;
        }
      }

      return false;
    },
  },
  getters: {
    getBingoCards(state): Card[] {
      return state.bingoCards;
    },
    isEndGame(state): boolean {
      for (const card of state.bingoCards) {
        if (!card.isCompleted) {
          return false;
        }
      }
      return true;
    },
    isFileRequired(state): boolean {
      return state.clientData.common.isFileRequired;
    },
    isScoreboard(state): boolean {
      return state.clientData.common.isScoreboard;
    },
  },
});
